import { FormattedMessage, useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef } from 'react'
import { makeSelectProjectId } from '../../../PlanSelection/selectors.js'
import { useGetPlansInfinityQuery } from '../../../../react-query/plan/query/use-get-plans-infinity-query.jsx'
import { plansApiTypeOptions } from '../../../../react-query/plan/constants.js'
import { typeFileQuickSelectOptions } from '../../../../components/stories/organisms/top-navigation/constants.js'
import { useOnInView } from 'react-intersection-observer'
import {
  EmptyData,
  EmptyFolderText,
  RecursiveListItemContainer
} from '../../../../components/stories/organisms/top-navigation/styled-components.js'
import NotFoundComponent from '../../../../components/stories/molecules/not-found/index.jsx'
import { NotFound } from '../../../../components/stories/atoms/icons/core/index.js'
import { useSelector } from 'react-redux'
import CustomFolderItem from './custom-folder-item.jsx'
import QuickSelectComponent from '../../../../components/stories/organisms/top-navigation/components/quick-select/index.jsx'
import { useHistory } from 'react-router-dom'
import TopBarLoadingContent from '../../../../components/stories/organisms/top-navigation/components/loading-content.jsx'

const CustomQuickSelectRecursiveList = ({
  folderComponent,
  itemComponent,
  onScrollDown,
  folderId,
  search,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { push } = useHistory()
  const projectId = useSelector(makeSelectProjectId())

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useGetPlansInfinityQuery({
      id: folderId ?? projectId,
      type: folderId
        ? plansApiTypeOptions.FOLDERS
        : plansApiTypeOptions.PROJECTS,
      search
    })

  const allData = data ? data?.pages.flatMap((d) => d.content) : []

  const isFolder = (type) => type === typeFileQuickSelectOptions.FOLDER

  const onBottomReached = useCallback(() => {
    hasNextPage && !isFetchingNextPage && fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const onBottomReachedRef = useRef(onBottomReached)

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  const bottomReachedRef = useOnInView(
    useCallback((inView) => {
      if (inView) {
        onBottomReachedRef.current?.()
      }
    }, []),
    {
      rootMargin: '20px',
      skip: !hasNextPage
    }
  )

  const handleClick = (item) => {
    push(`/project/${projectId}/${item.id}/plan`)
  }

  if (isLoading) {
    if (folderId) {
      return <QuickSelectComponent.LoadingItem />
    }

    return <TopBarLoadingContent />
  }

  if (allData.length === 0) {
    if (folderId) {
      return (
        <EmptyFolderText>
          <FormattedMessage id="empty_folder" />
        </EmptyFolderText>
      )
    }

    return (
      <EmptyData>
        <NotFoundComponent
          icon={<NotFound style={{ width: '60px', height: '60px' }} />}
          header={formatMessage({ id: 'no_result_found' })}
          description={formatMessage({ id: 'no_result_found_description' })}
        />
      </EmptyData>
    )
  }

  return (
    <RecursiveListItemContainer {...props}>
      {allData.map((item, index) => {
        if (isFolder(item.type)) {
          return (
            <CustomFolderItem item={item ?? null} key={item?.id ?? index} />
          )
        }

        return (
          <QuickSelectComponent.Item
            item={item ?? null}
            key={item?.id ?? index}
            handleCallback={handleClick}
          />
        )
      })}

      {isFetchingNextPage && <QuickSelectComponent.LoadingItem />}

      <div
        ref={bottomReachedRef}
        style={{
          height: '1px'
        }}
      />
    </RecursiveListItemContainer>
  )
}

CustomQuickSelectRecursiveList.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      route: PropTypes.string,
      children: PropTypes.array
    })
  )
}

export default CustomQuickSelectRecursiveList
